import React from 'react';
import { renderToString } from 'react-dom/server';
import { orderBy } from 'lodash-es';

import { apptopiaWatermark } from '../../../../../utils/charts/renderers';
import { format } from '../../../../../utils/date';
import { numberToHumanChart, numberToPercentage } from '../../../../../utils/formatter';

const tooltipPrecisionConfig = { precision: 1 };

export const marketShareConfig = ({
  chartType,
  unit = '%',
  activeItem,
  onClick,
  onMouseOver,
  toggleChartMask,
  isPress,
}) => {
  const isPercentage = unit === '%';
  const isColumn = chartType === 'column';

  return {
    chart: {
      type: chartType,
      ignoreHiddenSeries: true,
      spacing: [10, 0, 4, 0],
      height: 264,
      events: {
        load() {
          if (toggleChartMask) toggleChartMask();
        },
        render() {
          if (isPress) apptopiaWatermark.render(this);
        },
      },
    },
    yAxis: {
      title: null,
      tickAmount: 6,
      startOnTick: true,
      min: isColumn && !activeItem?.id ? 0 : null,
      max: isColumn && !activeItem?.id ? 100 : null,
      labels: {
        reserveSpace: true,
        style: {
          color: '#848EA1',
          fontSize: 12,
        },
        formatter() {
          if (isPercentage) {
            return `${this.value}%`;
          }
          return `${numberToHumanChart(this.value)}${unit}`;
        },
      },
      gridLineWidth: 1,
      gridLineColor: '#EDF0F5',
    },
    xAxis: {
      type: 'datetime',
      tickLength: 0,
      lineWidth: 0,
      startOnTick: false,
      showFirstLabel: true,
      crosshair: {
        color: '#E1E5EB',
        width: 1,
      },
      labels: {
        formatter() {
          if (this.isFirst || this.isLast) {
            return `<span class="text-nowrap">${format(this.value, 'MMM ‘YY')}</span>`;
          }
          return '';
        },
        style: {
          color: '#848EA1',
          fontSize: 12,
        },
        showFirstLabel: true,
        useHTML: true,
      },
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      borderWidth: 0,
      padding: 0,
      useHTML: true,
      outside: true,
      formatter() {
        if (!Array.isArray(this.points)) return false;

        const pointOptions = this.points[0].point.options;
        const isPartialElem = pointOptions.isPartial;
        const renderTooltipContent = (rowsContent) =>
          renderToString(
            <div
              style={{
                minWidth: '170px',
                borderRadius: '8px',
                border: '1px solid #E1E5EB',
                padding: '12px 16px',
                backgroundColor: '#fff',
                boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.08)',
              }}
            >
              <table className="table">
                <tr>
                  <td className="text-left text-black text-12 p-l-0 p-b-0 pr-md">
                    {format(this.x, 'MMM`YY')} {isPartialElem && '(Partial Period)'}
                  </td>
                </tr>
                {rowsContent}
              </table>
            </div>
          );

        return renderTooltipContent(
          orderBy(this.points, ['y'], ['desc']).map((point) => (
            <tr key={point.series.name}>
              <td className="text-left p-l-0">
                <span
                  className="v-a-m legend-bubble-marker"
                  style={{ backgroundColor: point.series.color || point.color, marginRight: 8 }}
                />
                <span
                  className="v-a-m d-inline-block text-truncate text-14"
                  style={{ maxWidth: 150 }}
                >
                  {point.series.name}
                </span>
              </td>
              <td
                className="text-right text-12 p-l-0 p-b-0"
                style={{ color: '#5D6B85', lineHeight: '150%' }}
              >
                <div className="text-bold text-black text-16">
                  <span>
                    {isPercentage
                      ? numberToPercentage(point.y, tooltipPrecisionConfig)
                      : `${numberToHumanChart(point.y, tooltipPrecisionConfig)}${unit}`}
                  </span>
                </div>
              </td>
            </tr>
          ))
        );
      },
    },
    title: {
      text: null,
    },
    plotOptions: {
      series: {
        animation: {
          duration: 750, // Initial animation duration
        },
        accessibility: { enabled: true },
        inactiveOtherPoints: false,
        zIndex: 10,
        stickyTracking: false,
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: false,
            },
          },
        },
        states: {
          hover: {
            enabled: true,
            brightness: 0,
          },
          inactive: {
            enabled: true,
            opacity: 0.2,
          },
        },
        events: {
          mouseOver() {
            if (onMouseOver) onMouseOver({ id: this.options.id });
          },
          click() {
            if (onClick) onClick({ id: this.options.id });
          },
        },
      },
      column: {
        minPointLength: 5,
        groupPadding: 0.05,
        borderRadius: 0,
        borderWidth: 0,
        stacking: isColumn ? 'normal' : undefined,
      },
      spline: {},
    },
  };
};

export const colors = [
  '#3E8EDE',
  '#F57F51',
  '#A26CBD',
  '#15CFD4',
  '#EA5782',
  '#45B068',
  '#A1CDF7',
  '#FFB296',
  '#CCB0EB',
  '#8AE7EA',
  '#F7ABB7',
  '#AFE09D',
  '#FCC047',
  '#748AB2',
  '#FCD78B',
  '#BEC9DB',
];
